<template>
    <v-card class="full-card overflow-y-auto" elevation="3">
        <v-card-text class="py-0 pt-3 text-start">
            <v-text-field
                v-model="filters['quick_search']['data']"
                append-inner-icon="mdi-magnify"
                autofocus
                density="compact"
                hide-details
                label="Quick Search"
                single-line
                variant="solo"
                @keydown.enter="fetchDevices"
                @click:append-inner="fetchDevices"
            />
            <v-select
                density="compact"
                label="Sort By"
                variant="solo"
                class="pt-3"
                v-model="filters['sort']['data']"
                :items="sortTypes"
                @update:model-value="sortClickCount=0"
            >
                <template v-slot:item="{ props, item }">
                    <v-list-item
                        :append-icon="sortingIcon(item.value)"
                        v-bind="props"
                        @click="updateSorting(item.value)"/>
                </template>
            </v-select>

            <div class="d-flex justify-space-between">
                <v-text-field
                    :append-icon="Object.keys(activeFilters).length? 'mdi mdi-close' : ''"
                    density="compact"
                    variant="plain"
                    readonly
                    @click:append="clearFilters">
                    <v-btn icon="mdi-filter-variant" size="small" variant="text"
                           @click="filtersDialogSwitch = true"/>
                </v-text-field>
                <v-btn
                    :icon="`mdi-view-${detailedView?'stream':'quilt'}`"
                    size="small" variant="text"
                    @click="switchView"
                />
            </div>
            <v-list v-if="Object.keys(activeFilters).length || (fleetFilter && fleetFilter !== 'total')" class="border pa-0" density="compact">
                <v-list-item v-if="fleetFilter && fleetFilter !== 'total'" class="py-0 border-b">
                    <div class="d-flex justify-space-between align-center">
                        <span>Fleet: {{ fleetFilterLabels[fleetFilter] }}</span>
                        <v-btn icon="mdi mdi-close" size="small" variant="plain" @click="emits('clearFleetFilter')"/>
                    </div>
                </v-list-item>
                <template v-for="(filter,key) in activeFilters"
                          :key="key">
                    <v-list-item v-if="!['sort','order'].includes(key)" class="py-0 border-b">
                        <div class="d-flex justify-space-between align-center">
                            <span>{{ filter.label }}</span>
                            <v-btn icon="mdi mdi-close" size="small" variant="plain" @click="removeFilter(key)"/>
                        </div>
                    </v-list-item>
                </template>
            </v-list>

            <v-list>
                <v-list-item v-for="(device,index) in displayedDevices" v-if="!processing && displayedDevices.length"
                             :id="`d-${device.slno}`"
                             @click="selectDevice(device)"
                             :class="{
                                 'bg-grey-lighten-1':device.id === selectedDevice?.id,
                                 'bg-grey-lighten-5':(index%2),
                                 ['status-' + device.status_color]: true
                             }"
                             class="px-1 rounded-sm device-item"
                             @dblclick="menuAction('zoom')"
                             @contextmenu.prevent="(event) => showContextMenu(event,device)"
                >
                    <div v-if="detailedView">
                        <div class="d-flex justify-space-between">
                            <div>
                                <span class="font-weight-bold text-body-2 me-1">{{ device.asset_label ?? device.slno }}</span>
                                <v-icon class="opacity-70" size="x-small" v-bind="signals(device.signal_bars??0)"/>
                                <v-icon class="opacity-70" size="x-small" v-bind="battery(device.battery_bars??0, primaryBatteryType(device))"/>
                                <template v-if="fuelPercent(device) != null">
                                    <v-icon size="x-small" icon="mdi-gas-station"
                                            :class="isLowFuel(device) ? '' : 'opacity-70'"
                                            :color="isLowFuel(device) ? 'red' : undefined"/>
                                    <small :class="isLowFuel(device) ? 'text-red font-weight-bold' : 'opacity-70'">{{ fuelPercent(device) }}%</small>
                                </template>
                                <small class="font-weight-bold">
                                    <v-tooltip v-if="!device.asset_driver_id" text="No driver assigned">
                                        <template v-slot:activator="{ props }">
                                            <v-icon class="me-1"
                                                    density="compact"
                                                    icon="mdi-alert-circle-outline"
                                                    size="x-small"
                                                    v-bind="props"
                                            />
                                        </template>
                                    </v-tooltip>
                                    <template v-if="deviceStatusFlags[device.asset_id]">
                                        <v-tooltip v-if="deviceStatusFlags[device.asset_id].checkEngine" text="Check Engine - View DTCs" location="top">
                                            <template #activator="{ props: tp }">
                                                <router-link :to="{path: `assets/${device.asset_id}`, query: {tab: 4}}" target="_blank" @click.stop>
                                                    <v-icon v-bind="tp" icon="mdi-engine" color="warning" size="x-small" class="me-1"/>
                                                </router-link>
                                            </template>
                                        </v-tooltip>
                                        <v-tooltip v-if="deviceStatusFlags[device.asset_id].inspectionDue" text="Inspection Due - View Inspections" location="top">
                                            <template #activator="{ props: tp }">
                                                <router-link :to="{path: `assets/${device.asset_id}`, query: {tab: 0}}" target="_blank" @click.stop>
                                                    <v-icon v-bind="tp" icon="mdi-clipboard-check-outline" color="warning" size="x-small" class="me-1"/>
                                                </router-link>
                                            </template>
                                        </v-tooltip>
                                        <v-tooltip v-if="deviceStatusFlags[device.asset_id].serviceDue" text="Service Due - View Maintenance" location="top">
                                            <template #activator="{ props: tp }">
                                                <router-link :to="{path: `assets/${device.asset_id}`, query: {tab: 1}}" target="_blank" @click.stop>
                                                    <v-icon v-bind="tp" icon="mdi-wrench-clock" color="warning" size="x-small" class="me-1"/>
                                                </router-link>
                                            </template>
                                        </v-tooltip>
                                    </template>
                                </small>
                            </div>
                            <div>
                                <v-chip
                                    v-if="device.motion_state && getEffectiveSpeed(device) > 2"
                                    :text="`${strOperations().unitifyDistance(getDisplaySpeedMph(device))}`"
                                    class="mr-2"
                                    rounded="lg"
                                    size="x-small"
                                    variant="outlined"
                                />
                                <v-chip
                                    :color="device.motion_state ? (getEffectiveSpeed(device) > 2 ? 'green' : 'warning') : 'default'"
                                    :text="device.motion_state ? (getEffectiveSpeed(device) > 2 ? 'In Motion' : 'Idling') : getStoppedLabel(device)"
                                    rounded="lg"
                                    size="x-small"
                                    variant="outlined"
                                />
                            </div>
                        </div>
                        <p class="text-caption text-grey">{{ device.slno }}</p>
                        <p class="text-caption">{{ device.location ?? device.formatted_address ?? '' }}</p>
                        <v-avatar v-if="device.asset_type_photo" class="me-2" rounded="lg" size="x-small">
                            <v-img :lazy-src="avatarPlaceholder" :src="device.asset_type_photo" aspect-ratio="1/1" cover
                                   height="100%"/>
                        </v-avatar>
                        <v-avatar v-if="device.asset_photo" class="me-2" rounded="lg" size="x-small">
                            <v-img :lazy-src="avatarPlaceholder" :src="device.asset_photo" aspect-ratio="1/1" cover
                                   height="100%"/>
                        </v-avatar>
                        <span v-if="device.driver_first_name">
                            <v-avatar class="me-2" rounded="lg" size="x-small">
                                <v-img
                                    :lazy-src="avatarPlaceholder"
                                    :src="device.driver_photo"
                                    aspect-ratio="1/1" cover height="100%"/>
                            </v-avatar>
                            <small>{{ device.driver_first_name }} {{ device.driver_last_name }}</small>
                            <v-chip v-if="device.driver_score != null" :color="driverScoreColor(device.driver_score)"
                                    size="x-small" variant="flat" class="ms-1 px-1" style="height:15px">
                                {{ device.driver_score }}
                            </v-chip>
                        </span>
                    </div>
                    <div v-else class="d-flex align-center">
                        <v-avatar v-if="device.driver_photo" class="me-2 flex-shrink-0" rounded="lg" size="24">
                            <v-img :lazy-src="avatarPlaceholder" :src="device.driver_photo" cover/>
                        </v-avatar>
                        <v-avatar v-else-if="device.asset_photo || device.asset_type_photo" class="me-2 flex-shrink-0" rounded="lg" size="24">
                            <v-img :lazy-src="avatarPlaceholder" :src="device.asset_photo || device.asset_type_photo" cover/>
                        </v-avatar>
                        <div class="text-truncate">
                            <v-tooltip v-if="!device.asset_driver_id" text="No driver assigned">
                                <template v-slot:activator="{ props }">
                                    <v-icon class="me-1"
                                            density="compact"
                                            icon="mdi-alert-circle-outline"
                                            size="x-small"
                                            v-bind="props"
                                    />
                                </template>
                            </v-tooltip>
                            <template v-if="deviceStatusFlags[device.asset_id]">
                                <v-tooltip v-if="deviceStatusFlags[device.asset_id].checkEngine" text="Check Engine" location="top">
                                    <template #activator="{ props: tp }">
                                        <v-icon v-bind="tp" icon="mdi-engine" color="warning" size="x-small" class="me-1"/>
                                    </template>
                                </v-tooltip>
                                <v-tooltip v-if="deviceStatusFlags[device.asset_id].inspectionDue" text="Inspection Due" location="top">
                                    <template #activator="{ props: tp }">
                                        <v-icon v-bind="tp" icon="mdi-clipboard-check-outline" color="warning" size="x-small" class="me-1"/>
                                    </template>
                                </v-tooltip>
                                <v-tooltip v-if="deviceStatusFlags[device.asset_id].serviceDue" text="Service Due" location="top">
                                    <template #activator="{ props: tp }">
                                        <v-icon v-bind="tp" icon="mdi-wrench-clock" color="warning" size="x-small" class="me-1"/>
                                    </template>
                                </v-tooltip>
                            </template>
                            {{ device.asset_label ?? device.slno }}
                            <small v-if="device.driver_first_name" class="text-grey ms-1">{{ device.driver_first_name }}</small>
                            <v-chip v-if="device.driver_first_name && device.driver_score != null"
                                    :color="driverScoreColor(device.driver_score)"
                                    size="x-small" variant="flat" class="ms-1 px-1" style="height:15px">
                                {{ device.driver_score }}
                            </v-chip>
                        </div>
                    </div>
                </v-list-item>
                <v-list-item v-else class="text-center">
                    <v-skeleton-loader v-if="processing" type="paragraph"/>
                    <p v-else>No device found</p>
                </v-list-item>
                <v-menu v-if="menu.state"
                        v-model="menu.state"
                        :target="menu.position"
                        density="compact"
                >
                    <v-list class="pa-0" density="compact">
                        <v-list-item
                            v-if="preSelectedDevice.lat && preSelectedDevice.lng"
                            @click="menuAction('copyLocation')">
                            {{ `${preSelectedDevice.lat}, ${preSelectedDevice.lng}` }}
                            <i class="mdi mdi-content-copy"></i>
                        </v-list-item>
                        <v-list-item
                            v-if="preSelectedDevice.lat && preSelectedDevice.lng"
                            @click="menuAction('findNearest')">Find Nearest Device
                        </v-list-item>
                        <v-list-item
                            v-if="preSelectedDevice.asset_id"
                            :href="`trips-report/${preSelectedDevice.asset_id}/daily/${preSelectedDevice.company}`"
                            target="_blank">
                            Show Daily Report
                        </v-list-item>
                        <v-list-item
                            v-if="preSelectedDevice.asset_id"
                            :href="`trips-report/${preSelectedDevice.asset_id}/monthly/${preSelectedDevice.company}`"
                            target="_blank">
                            Show Monthly Report
                        </v-list-item>
                        <v-list-item @click="menuAction('zoom')">Zoom To</v-list-item>
                        <v-list-item
                            v-if="preSelectedDevice.lat && preSelectedDevice.lng"
                            @click="menuAction('streetView')">Street View
                        </v-list-item>
                        <v-list-item
                            v-if="preSelectedDevice.lat && preSelectedDevice.lng"
                            @click="menuAction('direction')">Direction
                        </v-list-item>
                        <v-list-item :href="`assets/${preSelectedDevice.asset_id}`" target="_blank">
                            {{ preSelectedDevice.asset_label }} Details
                        </v-list-item>
                        <v-list-item :href="`devices/${preSelectedDevice.slno}`" target="_blank">
                            Device Details
                        </v-list-item>
                    </v-list>
                </v-menu>
            </v-list>
        </v-card-text>
    </v-card>

    <filters-dialog
        v-if="filtersDialogSwitch"
        v-model="filtersDialogSwitch"
        :filters="filters"

        @applyFilters="fetchDevices"
        @resetFilters="clearFilters"
    />
</template>

<script setup>
import {computed, onBeforeMount, onMounted, onUnmounted, reactive, ref, watch} from "vue";
import {send} from "@/composables/apiClient";
import {strOperations} from "@/composables/globals";
import {useClipboard} from "@/composables/clipboard";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {filterDevicesByFleetStatus, fleetFilterLabels, getStoppedLabel, isServerFilter, fuelPercent, isLowFuel} from "@/composables/fleetStatus";
import avatarPlaceholder from "@/assets/images/avatar.png";
import {powerSourceIcon, primaryBatteryType} from "@/composables/usePowerSourceIcon";
import FiltersDialog from "@/components/cards/devices/FiltersDialog.vue";

const detailedView = ref(1);
const processing = ref(true);
const snackbar = useSnackbarStore();
const sortClickCount = ref(0);
const selectedDevice = ref(null);
const preSelectedDevice = ref(null);
const {copyToClipboard} = useClipboard();
const props = defineProps(['newDeviceSelection', 'companyLocations', 'fleetFilter', 'serverFleetCounts']);
const emits = defineEmits(['loaded', 'selectDevice', 'zoomToDevice', 'nearestToDevice', 'clearFleetFilter'])

const devices = ref([]);
// Raw, and deliberately so: the moving/idle boundaries below compare against
// it and must stay in step with the server's own raw thresholds.
const getEffectiveSpeed = (device) => Math.max(device.gps_speed_raw ?? device.gps_speed ?? 0, device.obd_present ? (device.obd_speed ?? 0) : 0);
// What to show: prefer the server-converted speed_mph, since the raw column
// carries MPH from hardware and km/h x2 from phones. Falls back to the raw
// value, which is what was displayed before.
// speed_mph covers gps_speed ONLY; fold the OBD half back in so the number
// matches the status chip beside it.
const getDisplaySpeedMph = (device) => Math.max(
    strOperations().speedToMph(device, device.gps_speed_raw ?? device.gps_speed ?? 0),
    device.obd_present ? (device.obd_speed ?? 0) : 0
);
const serverAssetIdsMap = computed(() => {
    const map = {};
    (props.serverFleetCounts || []).forEach(item => {
        if (item.asset_ids) {
            map[item.key] = new Set(item.asset_ids);
        }
    });
    return map;
});
const getWarningCount = (device) => {
    const id = device.asset_id;
    let count = 0;
    if (serverAssetIdsMap.value.check_engine?.has(id)) count++;
    if (serverAssetIdsMap.value.pending_inspection?.has(id)) count++;
    if (serverAssetIdsMap.value.pending_maintenance?.has(id)) count++;
    if (!device.driver_first_name) count++;
    return count;
};
const displayedDevices = computed(() => {
    const filtered = filterDevicesByFleetStatus(devices.value, props.fleetFilter, serverAssetIdsMap.value);
    if (filters['sort']['data'] === 'warnings') {
        return [...filtered].sort((a, b) => getWarningCount(b) - getWarningCount(a));
    }
    return filtered;
});
const deviceStatusFlags = computed(() => {
    const flags = {};
    (devices.value || []).forEach(device => {
        const id = device.asset_id;
        flags[id] = {
            checkEngine: !!serverAssetIdsMap.value.check_engine?.has(id),
            inspectionDue: !!serverAssetIdsMap.value.pending_inspection?.has(id),
            serviceDue: !!serverAssetIdsMap.value.pending_maintenance?.has(id),
        };
    });
    return flags;
});
const headers = ref([]);
const drivers = ref([]);
const assetGroups = ref([]);
const productSkus = ref([]);
const sortTypes = [
    {title: 'Serial no.', value: 'slno'},
    {title: 'Asset', value: 'asset_label'},
    {title: 'Last Update', value: 'last_updated'},
    {title: 'Warnings First', value: 'warnings'},
];
const deviceStatuses = ref([
    {id: 0, title: 'Active'},
    {id: 1, title: 'Disabled'},
    {id: 2, title: 'Faulty'},
    {id: 3, title: 'Lost'}
]);
const menu = ref({state: 0});
const filtersDialogSwitch = ref(false);
const filters = reactive({
    sort: {label: 'Sort by', data: null, type: 'hidden'},
    order: {label: 'Order by', data: null, type: 'hidden'},
    quick_search: {label: 'Quick Search', data: null, type: 'hidden'},
    label: {label: 'Asset Name', data: null},
    serial_num: {label: 'Serial Number', data: null, action: null},
    motion_state: {
        label: 'Device State', type: 'select', data: null, source: [
            {title: 'Parked', value: 0},
            {title: 'Moving', value: 1},
        ]
    },
    device_status: {
        label: 'Device Status', type: 'select', data: null, title: 'title', value: 'id', source: deviceStatuses
    },
    driver: {
        label: 'Driver',
        type: 'select',
        data: null,
        title: 'first_name',
        value: 'id',
        multiple: true,
        source: drivers
    },
    company_location: {
        label: 'Company Locations',
        type: 'select',
        data: null,
        title: 'name',
        value: 'id',
        multiple: true,
        source: props.companyLocations
    },
    asset_group: {
        label: 'Asset Group',
        type: 'select',
        data: null,
        title: 'name',
        value: 'id',
        multiple: true,
        source: assetGroups
    },
    geo_radius: {label: 'Radius', type: 'number', data: null, latitude: null, longitude: null},
    sku: {label: 'SKU', type: 'select', data: null, title: 'sku', value: 'id', source: productSkus},
    imei: {label: 'IMEI', data: null, action: null},
    iccid: {label: 'ICC ID', data: null, action: null},
    wifi_mac: {label: 'WIFI MAC', type: 'text', data: null},
    bt_mac: {label: 'BT MAC', type: 'text', data: null},
    lora_mac: {label: 'LORA MAC', type: 'text', data: null},
    voltage1: {label: 'Device Voltage 1', type: 'number', data: null, action: null},
    voltage2: {label: 'Device Voltage 2', type: 'number', data: null, action: null},
    last_update: {label: 'Last Updated', data: null, action: null, type: 'datetime-local'},
});
const activeFilters = computed(() => {
    const activatedFilters = {};
    for (const [key, filter] of Object.entries(filters)) {
        if (filter) {
            if (filter.data !== null) {
                activatedFilters[key] = filter;
            }
        }
    }
    return activatedFilters;
});

const resetSelectedDevice = () => {
    selectedDevice.value = null;
    emits('selectDevice', null);
}
const signals = (strength) => {
    switch (strength) {
        case 0:
            return {
                icon: 'mdi-signal-off',
                color: '#dbdbdb'
            }
        case 1:
            return {
                icon: 'mdi-signal-cellular-outline',
                color: '#d70000'
            }
        case 2:
            return {
                icon: 'mdi-signal-cellular-1',
                color: '#ff9e01'
            }
        case 3:
            return {
                icon: 'mdi-signal-cellular-2',
                color: '#849e00'
            }
        case 4:
            return {
                icon: 'mdi-signal-cellular-3',
                color: '#099f00'
            }
        case 5:
            return {
                icon: 'mdi-signal-cellular-3',
                color: '#099f00'
            }
    }
}
const battery = (strength, type = null) => {
    return powerSourceIcon(strength, type)
}
const sortingIcon = (val) => {
    if (filters['sort']['data'] === val) {
        switch (sortClickCount.value) {
            case 1:
                return 'mdi-sort-ascending';
            case 2:
                return 'mdi-sort-descending';
            default:
                return 'mdi-sort';
        }
    }
    return 'mdi-sort';
}
const updateSorting = (val) => {
    // Warnings sort is client-side only
    if (val === 'warnings') {
        filters['sort']['data'] = 'warnings';
        filters['order']['data'] = null;
        sortClickCount.value = 1;
        return;
    }

    // Make first click of last update sort by desc
    if (val === 'last_updated') {
        sortClickCount.value = (sortClickCount.value + 1) % 3;
    }

    sortClickCount.value++;
    switch (sortClickCount.value) {
        case 1:
            filters['order']['data'] = 'asc';
            break;
        case 2:
            filters['order']['data'] = 'desc';
            break;
        default:
            val = null;
            sortClickCount.value = 0;
            filters['order']['data'] = null;
            break;
    }

    filters['sort']['data'] = val;
    fetchDevices();
}
const selectDevice = (device) => {
    selectedDevice.value = device;
    emits('selectDevice', device);
}
const showContextMenu = (e, device) => {
    e.preventDefault();
    menu.value = {
        state: true,
        position: [e.clientX, e.clientY]
    };
    preSelectedDevice.value = device;
}
const menuAction = (type) => {
    if (!preSelectedDevice.value && selectedDevice.value) {
        preSelectedDevice.value = selectedDevice.value;
    }
    if (preSelectedDevice.value) {
        switch (type) {
            case 'copyLocation':
                copyToClipboard(`${preSelectedDevice.value.lat}, ${preSelectedDevice.value.lng}`);
                break;
            case 'findNearest':
                emits('nearestToDevice', preSelectedDevice.value)
                break;
            case 'zoom':
                emits('zoomToDevice', preSelectedDevice.value)
                break;
            case 'direction':
                window.open(`http://maps.google.com/maps/dir//${preSelectedDevice.value.lat},${preSelectedDevice.value.lng}`,
                    "_blank");
                break;
            case 'streetView':
                window.open(`http://maps.google.com/maps?q=${preSelectedDevice.value.lat},${preSelectedDevice.value.lng}&layer=t&cbll=${preSelectedDevice.value.lat},${preSelectedDevice.value.lng}&cbp=12,0,0,0,0&output=classic`,
                    "_blank");
                break;
        }
    }
}
const switchView = () => {
    detailedView.value = !detailedView.value;
}
const removeFilter = (key) => {
    filters[key]['data'] = null;
    filters[key]['action'] ? filters[key]['action'] = null : '';
    if (Object.keys(activeFilters.value).length) {
        fetchDevices();
    } else {
        clearFilters();
    }
}
const clearFilters = (callApi = true) => {
    Object.keys(filters).forEach((key) => {
        ['data', 'action', 'latitude', 'longitude'].forEach(field => {
            if (filters[key] && filters[key].hasOwnProperty(field)) {
                filters[key][field] = null;
            }
        });
    });
    if (callApi) {
        fetchDevices();
    }
}
const fetchProductSkusList = () => {
    send('get', `product-skus`, {
        list: true,
        portals: true,
        columns: JSON.stringify("id,sku")
    })
        .then((response) => {
            productSkus.value = response.data.data.product_skus;
        }).catch((error) => {
        snackbar.showSnackbar('Product skus list could not be loaded', 'error')
    })
}
// Driver safety-score chip colour, matching the live-map popup (DevicesMap).
const driverScoreColor = (score) => {
    if (score >= 8.0) return 'success'
    if (score >= 5.0) return 'warning'
    return 'error'
}
const fetchDevices = ({preserveSelection = false} = {}) => {
    const previousSelectedId = preserveSelection ? selectedDevice.value?.id : null;
    if (!preserveSelection) {
        devices.value = [];
    }
    processing.value = true;
    let filtered = Object.values(activeFilters.value).length;
    resetSelectedDevice();
    // approximate=1: include a just-activated device's seed position (flagged
    // approximate) so the map can draw it faded instead of hiding it. GET passes
    // it as a query param; the filtered POST keeps filters as the body and carries
    // the flag in the URL.
    send(`${filtered ? 'post' : 'get'}`,
        `devices${filtered ? '/filtered?approximate=1' : ''}`,
        filtered ? filters : {approximate: 1})
        .then((response) => {
            devices.value = response.data.data.devices;
            headers.value = response.data.data.headers;
            emits('loaded', {devices: devices.value, headers: headers.value})
            if (devices.value.length) {
                // Restore previous selection if it still exists in the refreshed list
                const restored = previousSelectedId
                    ? devices.value.find(d => d.id === previousSelectedId)
                    : null;
                selectDevice(restored || devices.value[0]);
            }
        })
        .catch((error) => {
            snackbar.showSnackbar('Devices could not be loaded', 'error')
        }).finally(() => processing.value = false);
}
const fetchDriversList = async () => {
    await send('get', `companies/drivers?list=true`)
        .then((response) => {
            drivers.value = response.data.data;
            filters['driver']['source'] = drivers.value
        }).catch((error) => {
            console.log(error)
            snackbar.showSnackbar('Drivers list could not be loaded', 'error')
        })
}
const fetchAssetGroupsList = async () => {
    await send('get', `asset-groups?list=true`)
        .then((response) => {
            assetGroups.value = response.data.data;
            filters['asset_group']['source'] = assetGroups.value
        }).catch((error) => {
            console.log(error)
            snackbar.showSnackbar('Asset Group could not be loaded', 'error')
        })
}

watch(() => props.companyLocations, (locations) => {
    filters['company_location']['source'] = locations;
});
watch(() => props.newDeviceSelection, (newDevice) => {
    if (newDevice && selectedDevice.value?.id !== newDevice.id) {
        selectedDevice.value = newDevice;
        document.getElementById(`d-${newDevice.slno}`).scrollIntoView({behavior: 'smooth'});
    }
})

onBeforeMount(() => {
    fetchProductSkusList()
    fetchDevices()
})
const onMqttTokenRefresh = () => fetchDevices();
const onCompanyDataReload = () => fetchDevices({preserveSelection: true});
onMounted(() => {
    fetchAssetGroupsList()
    fetchDriversList()
    window.addEventListener('mqtt-token-refresh', onMqttTokenRefresh);
    window.addEventListener('company-data-reload', onCompanyDataReload);
})
onUnmounted(() => {
    window.removeEventListener('mqtt-token-refresh', onMqttTokenRefresh);
    window.removeEventListener('company-data-reload', onCompanyDataReload);
    const localStorageProfiles = JSON.parse(localStorage.getItem('filterProfiles'));
    if (localStorageProfiles?.selectedProfile) {
        localStorage.setItem('filterProfiles', JSON.stringify({
            selectedProfile: null,
            profiles: localStorageProfiles.profiles,
        }));
    }
})
</script>

<style>
.device-item {
    border-width: 1px !important;
    border-bottom: 1px solid rgb(0 0 0 / 12%);
}

.device-item:hover {
    background-color: #dcdcdc;
}

.cursor-pointer .v-field:hover, .cursor-pointer input:hover {
    cursor: pointer !important;
}

.status-blue {
    border-bottom-color: rgba(33, 117, 243, 0.6) !important;
}

.status-red {
    border-bottom-color: rgba(215, 0, 0, 0.6) !important;
}

.status-amber {
    border-bottom-color: rgba(255, 158, 1, 0.6) !important;
}
</style>
